import React, { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Nav, Row, Table } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { commitImport } from '../../services/Configs/ExcelImports';

const getUserAndRole = () => {
    const roleId = sessionStorage.getItem('roleId')
        ? parseInt(sessionStorage.getItem('roleId'), 10)
        : 0;

    return { roleId };
};

const normalizeValue = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const clean = String(value).trim();
    return clean === '' ? null : clean;
};

const normalizeHeader = (value) => {
    if (value === undefined || value === null) return '';
    return String(value).trim().toUpperCase();
};

const formatExcelDate = (value) => {
    if (!value) return null;

    if (value instanceof Date && !isNaN(value)) {
        return value.toISOString().slice(0, 10);
    }

    if (typeof value === 'number') {
        const parsed = XLSX.SSF.parse_date_code(value);
        if (!parsed) return String(value);

        const yyyy = parsed.y;
        const mm = String(parsed.m).padStart(2, '0');
        const dd = String(parsed.d).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    const str = String(value).trim();

    if (str.includes('/')) {
        const parts = str.split('/');
        if (parts.length === 3) {
            const a = parts[0].padStart(2, '0');
            const b = parts[1].padStart(2, '0');
            const c = parts[2];

            if (c.length === 4) {
                return `${c}-${a}-${b}`;
            }
        }
    }

    return str;
};

const parsePrice = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const parsed = Number(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isNaN(parsed) ? null : parsed;
};

const normalizeFolio = (value) => {
    if (value === undefined || value === null || value === '') return null;

    const raw = String(value).trim();
    if (!raw) return null;

    const upper = raw.toUpperCase();

    if (upper.startsWith('NN-')) return `Nn-${raw.substring(3).trim()}`;
    if (upper.startsWith('NN')) return `Nn-${raw.substring(2).replace(/^-/, '').trim()}`;
    if (upper.startsWith('FF-')) return `FF-${raw.substring(3).trim()}`;
    if (upper.startsWith('FF')) return `FF-${raw.substring(2).replace(/^-/, '').trim()}`;

    if (/^\d+$/.test(raw)) {
        return `Nn-${raw}`;
    }

    return raw;
};

const getFolioType = (folio) => {
    const value = (folio || '').toUpperCase();
    if (value.startsWith('NN-')) return 'Nn';
    if (value.startsWith('FF-')) return 'FF';
    return 'UNKNOWN';
};

const validateInventoryRow = (row) => {
    if (!row.nombre && !row.folio) {
        return { valid: false, message: 'Fila vacía' };
    }

    if (!row.nombre) {
        return { valid: false, message: 'Falta nombre' };
    }

    if (!row.folio) {
        return { valid: false, message: 'Falta folio' };
    }

    if (row.folio_type === 'UNKNOWN') {
        return { valid: false, message: 'Prefijo de folio no reconocido' };
    }

    return { valid: true, message: 'Lista para importar' };
};

const REQUIRED_HEADERS = ['NOMBRE', 'FOLIO', 'FECHA ENTRADA', 'ESTADO'];

const findHeaderRowIndex = (rows) => {
    for (let i = 0; i < Math.min(rows.length, 6); i++) {
        const normalizedRow = (rows[i] || []).map(normalizeHeader);
        const hasRequiredHeaders = REQUIRED_HEADERS.every((header) =>
            normalizedRow.includes(header)
        );

        if (hasRequiredHeaders) return i;
    }

    return -1;
};

const buildInventorySheet = (sheetName, matrixRows) => {
    const headerRowIndex = findHeaderRowIndex(matrixRows);

    if (headerRowIndex === -1) {
        return null;
    }

    const headers = (matrixRows[headerRowIndex] || []).map(normalizeHeader);
    const dataRows = matrixRows.slice(headerRowIndex + 1);

    const rows = dataRows
        .map((row, index) => {
            const rowObj = {};

            headers.forEach((header, headerIndex) => {
                rowObj[header] = row?.[headerIndex] ?? null;
            });

            const nombre = normalizeValue(rowObj['NOMBRE']);
            const folio = normalizeFolio(rowObj['FOLIO']);
            const fecha_entrada = formatExcelDate(rowObj['FECHA ENTRADA']);
            const estado_excel = normalizeValue(rowObj['ESTADO']);
            const fecha_salida = formatExcelDate(rowObj['FECHA SALIDA']);
            const canal_venta = normalizeValue(rowObj['CANAL DE VENTA']);
            const cliente = normalizeValue(rowObj['CLIENTE']);
            const metodo_pago = normalizeValue(rowObj['METODO DE PAGO']);
            const precio = parsePrice(rowObj['PRECIO']);
            const fecha_compra = formatExcelDate(rowObj['FECHA DE COMPRA']);

            const mappedRow = {
                tempId: `${sheetName}-${index + 1}`,
                rowNumber: headerRowIndex + index + 2,
                sheetName,
                nombre,
                folio,
                fecha_entrada,
                estado_excel,
                fecha_salida,
                canal_venta,
                cliente,
                metodo_pago,
                precio,
                fecha_compra,
                folio_type: getFolioType(folio),
            };

            return {
                ...mappedRow,
                validation: validateInventoryRow(mappedRow),
            };
        })
        .filter((row) => row.nombre || row.folio);

    return {
        name: sheetName,
        type: 'inventory',
        rows,
        rawRows: matrixRows,
        headerRowIndex,
        titleRow: headerRowIndex > 0 ? matrixRows[headerRowIndex - 1] : null,
    };
};

const buildGenericSheet = (sheetName, matrixRows) => {
    const headers = (matrixRows[0] || []).map((cell, index) => {
        const value = normalizeValue(cell);
        return value || `Columna ${index + 1}`;
    });

    const rows = matrixRows
        .slice(1)
        .map((row, index) => {
            const values = {};
            headers.forEach((header, headerIndex) => {
                values[header] = row?.[headerIndex] ?? null;
            });

            return {
                rowNumber: index + 1,
                values,
            };
        })
        .filter((row) =>
            Object.values(row.values).some((value) => value !== null && value !== '')
        );

    return {
        name: sheetName,
        type: 'generic',
        rows,
        rawRows: matrixRows,
    };
};

const ExcelReports = () => {
    const { roleId } = getUserAndRole();

    const [selectedFile, setSelectedFile] = useState(null);
    const [sheets, setSheets] = useState([]);
    const [activeSheetName, setActiveSheetName] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [saving, setSaving] = useState(false);

    const activeSheet = useMemo(() => {
        return sheets.find((sheet) => sheet.name === activeSheetName) || null;
    }, [sheets, activeSheetName]);

    const summary = useMemo(() => {
        if (!activeSheet || activeSheet.type !== 'inventory') {
            return {
                total: activeSheet?.rows?.length || 0,
                valid: 0,
                invalid: 0,
                nn: 0,
                ff: 0,
            };
        }

        const validRows = activeSheet.rows.filter((r) => r.validation?.valid);
        const invalidRows = activeSheet.rows.filter((r) => !r.validation?.valid);
        const nnRows = activeSheet.rows.filter((r) => r.folio_type === 'Nn');
        const ffRows = activeSheet.rows.filter((r) => r.folio_type === 'FF');

        return {
            total: activeSheet.rows.length,
            valid: validRows.length,
            invalid: invalidRows.length,
            nn: nnRows.length,
            ff: ffRows.length,
        };
    }, [activeSheet]);

    const resetState = () => {
        setSelectedFile(null);
        setSheets([]);
        setActiveSheetName('');
        setErrorMessage('');
        setSuccessMessage('');
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        setErrorMessage('');
        setSuccessMessage('');
        setSelectedFile(file || null);
        setSheets([]);
        setActiveSheetName('');

        if (!file) return;

        try {
            console.log('[ExcelImport] Archivo seleccionado:', {
                name: file.name,
                sizeBytes: file.size,
                sizeMB: (file.size / (1024 * 1024)).toFixed(2)
            });

            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });

            console.log('[ExcelImport] Pestañas detectadas:', workbook.SheetNames);

            const parsedSheets = workbook.SheetNames.map((sheetName) => {
                const worksheet = workbook.Sheets[sheetName];

                const matrixRows = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    defval: null,
                    raw: true,
                });

                const inventorySheet = buildInventorySheet(sheetName, matrixRows);
                if (inventorySheet) {
                    console.log(`[ExcelImport] Pestaña importable: ${sheetName}`, {
                        rows: inventorySheet.rows.length,
                        headerRowIndex: inventorySheet.headerRowIndex
                    });
                    return inventorySheet;
                }

                const genericSheet = buildGenericSheet(sheetName, matrixRows);
                console.log(`[ExcelImport] Pestaña genérica: ${sheetName}`, {
                    rows: genericSheet.rows.length
                });
                return genericSheet;
            });

            setSheets(parsedSheets);

            const preferredSheet =
                parsedSheets.find((sheet) => sheet.type === 'inventory' && sheet.rows.length > 0) ||
                parsedSheets.find((sheet) => sheet.type === 'inventory') ||
                parsedSheets[0] ||
                null;

            if (preferredSheet) {
                setActiveSheetName(preferredSheet.name);
            }

            const totalInventoryRows = parsedSheets
                .filter((sheet) => sheet.type === 'inventory')
                .reduce((acc, sheet) => acc + sheet.rows.length, 0);

            console.log('[ExcelImport] Resumen de lectura:', {
                totalSheets: parsedSheets.length,
                inventorySheets: parsedSheets.filter((s) => s.type === 'inventory').length,
                totalInventoryRows
            });

            setSuccessMessage(
                `Archivo cargado correctamente. Se detectaron ${parsedSheets.length} pestaña(s).`
            );
        } catch (error) {
            console.error('[ExcelImport] Error al leer archivo:', error);
            setErrorMessage('No fue posible leer el archivo. Verifica que sea un Excel válido.');
        }
    };

    const handleSaveImport = async () => {
        setErrorMessage('');
        setSuccessMessage('');

        if (!activeSheet || activeSheet.type !== 'inventory') {
            setErrorMessage('La pestaña activa no es importable.');
            return;
        }

        const validRows = activeSheet.rows.filter((row) => row.validation?.valid);

        if (!validRows.length) {
            setErrorMessage('No hay registros válidos en la pestaña activa.');
            return;
        }

        const payload = {
            fileName: selectedFile?.name || null,
            sheets: [
                {
                    name: activeSheet.name,
                    rows: validRows.map((row) => ({
                        rowNumber: row.rowNumber,
                        nombre: row.nombre,
                        folio: row.folio,
                        fecha_entrada: row.fecha_entrada,
                        estado_excel: row.estado_excel,
                        fecha_salida: row.fecha_salida,
                        canal_venta: row.canal_venta,
                        cliente: row.cliente,
                        metodo_pago: row.metodo_pago,
                        precio: row.precio,
                        fecha_compra: row.fecha_compra,
                        folio_type: row.folio_type
                    }))
                }
            ]
        };

        try {
            setSaving(true);

            const payloadSize = new Blob([JSON.stringify(payload)]).size;

            console.log('[ExcelImport] Enviando pestaña activa:', {
                sheetName: activeSheet.name,
                totalRows: validRows.length,
                payloadSizeBytes: payloadSize,
                payloadSizeMB: (payloadSize / (1024 * 1024)).toFixed(2)
            });

            const response = await commitImport(payload);

            console.log('[ExcelImport] Respuesta backend:', response);

            if (!response?.result) {
                setErrorMessage(response?.message || 'Ocurrió un error al guardar la importación.');
                return;
            }

            setSuccessMessage(
                `Importación guardada correctamente para la pestaña "${activeSheet.name}". Registros enviados: ${validRows.length}.`
            );
        } catch (error) {
            console.error('[ExcelImport] Error al guardar:', error);
            setErrorMessage('Ocurrió un error al guardar la importación.');
        } finally {
            setSaving(false);
        }
    };

    if (roleId !== 1) {
        return (
            <Alert variant="danger" className="mb-0">
                No tienes permisos para acceder a este módulo.
            </Alert>
        );
    }

    const genericHeaders =
        activeSheet?.type === 'generic' && activeSheet.rows.length
            ? Object.keys(activeSheet.rows[0]?.values || {})
            : [];

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h2 className="mb-1">Importación de Excel</h2>
                    <div className="text-muted">
                        Carga archivos para previsualizar y preparar la importación al sistema.
                    </div>
                </div>
            </div>

            {errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}
            {successMessage ? <Alert variant="success">{successMessage}</Alert> : null}

            <Row className="g-3 mb-3">
                <Col xs={12} xl={7}>
                    <Card className="shadow-sm border-0">
                        <Card.Body>
                            <h5 className="mb-3">Cargar archivo</h5>

                            <Form.Group controlId="excelFile">
                                <Form.Label>Selecciona un archivo Excel</Form.Label>
                                <Form.Control
                                    type="file"
                                    accept=".xlsx,.xls,.csv"
                                    onChange={handleFileChange}
                                />
                            </Form.Group>

                            <div className="mt-3 text-muted">
                                {selectedFile ? (
                                    <>
                                        <strong>Archivo:</strong> {selectedFile.name}
                                    </>
                                ) : (
                                    'Aún no has seleccionado un archivo.'
                                )}
                            </div>

                            <div className="d-flex gap-2 mt-4">
                                <Button
                                    variant="success"
                                    onClick={handleSaveImport}
                                    disabled={saving || !sheets.length}
                                >
                                    {saving ? 'Guardando...' : 'Guardar importación'}
                                </Button>

                                <Button
                                    variant="outline-secondary"
                                    onClick={resetState}
                                    disabled={saving}
                                >
                                    Limpiar
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} xl={5}>
                    <Card className="shadow-sm border-0">
                        <Card.Body>
                            <h5 className="mb-3">Resumen</h5>

                            <div className="d-flex flex-wrap gap-2">
                                <Badge bg="secondary">Total: {summary.total}</Badge>
                                <Badge bg="success">Válidos: {summary.valid}</Badge>
                                <Badge bg="danger">Inválidos: {summary.invalid}</Badge>
                                <Badge bg="primary">Nn: {summary.nn}</Badge>
                                <Badge bg="warning" text="dark">FF: {summary.ff}</Badge>
                            </div>

                            <hr />

                            <div className="small text-muted">
                                {activeSheet?.type === 'inventory'
                                    ? `La pestaña "${activeSheet.name}" tiene estructura de importación.`
                                    : activeSheet
                                        ? `La pestaña "${activeSheet.name}" no coincide con el formato de importación, pero sí puede visualizarse.`
                                        : 'Carga un archivo para visualizar sus pestañas.'}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {!!sheets.length && (
                <Card className="shadow-sm border-0 mb-3">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Pestañas del archivo</h5>
                            <div className="text-muted small">
                                {sheets.length} pestaña(s)
                            </div>
                        </div>

                        <Nav variant="tabs" className="flex-nowrap overflow-auto">
                            {sheets.map((sheet) => (
                                <Nav.Item key={sheet.name}>
                                    <Nav.Link
                                        active={activeSheetName === sheet.name}
                                        onClick={() => setActiveSheetName(sheet.name)}
                                        style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    >
                                        {sheet.name}{' '}
                                        {sheet.type === 'inventory' ? (
                                            <Badge bg="success" className="ms-1">Importable</Badge>
                                        ) : (
                                            <Badge bg="secondary" className="ms-1">Vista</Badge>
                                        )}
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                    </Card.Body>
                </Card>
            )}

            <Card className="shadow-sm border-0">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                            Vista previa del archivo
                            {activeSheet ? ` - ${activeSheet.name}` : ''}
                        </h5>
                        <div className="text-muted small">
                            {activeSheet?.rows?.length || 0} registro(s)
                        </div>
                    </div>

                    {!activeSheet ? (
                        <div className="text-center py-4 text-muted">
                            No hay datos cargados.
                        </div>
                    ) : activeSheet.type === 'inventory' ? (
                        <>
                            {activeSheet.titleRow ? (
                                <div className="mb-3 fw-semibold text-uppercase text-center">
                                    {activeSheet.titleRow.filter(Boolean).join(' ')}
                                </div>
                            ) : null}

                            <div className="table-responsive">
                                <Table bordered hover className="align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Nombre</th>
                                            <th>Folio</th>
                                            <th>Tipo</th>
                                            <th>Fecha Entrada</th>
                                            <th>Estado</th>
                                            <th>Fecha Salida</th>
                                            <th>Canal de Venta</th>
                                            <th>Cliente</th>
                                            <th>Método de Pago</th>
                                            <th>Precio</th>
                                            <th>Fecha Compra</th>
                                            <th>Validación</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeSheet.rows.length === 0 ? (
                                            <tr>
                                                <td colSpan={13} className="text-center py-4">
                                                    No hay datos cargados en esta pestaña.
                                                </td>
                                            </tr>
                                        ) : (
                                            activeSheet.rows.map((row) => (
                                                <tr key={row.tempId}>
                                                    <td>{row.rowNumber}</td>
                                                    <td>{row.nombre || '—'}</td>
                                                    <td>{row.folio || '—'}</td>
                                                    <td>
                                                        <Badge
                                                            bg={
                                                                row.folio_type === 'Nn'
                                                                    ? 'primary'
                                                                    : row.folio_type === 'FF'
                                                                        ? 'warning'
                                                                        : 'secondary'
                                                            }
                                                            text={row.folio_type === 'FF' ? 'dark' : undefined}
                                                        >
                                                            {row.folio_type}
                                                        </Badge>
                                                    </td>
                                                    <td>{row.fecha_entrada || '—'}</td>
                                                    <td>{row.estado_excel || '—'}</td>
                                                    <td>{row.fecha_salida || '—'}</td>
                                                    <td>{row.canal_venta || '—'}</td>
                                                    <td>{row.cliente || '—'}</td>
                                                    <td>{row.metodo_pago || '—'}</td>
                                                    <td>
                                                        {row.precio !== null
                                                            ? `$${Number(row.precio).toLocaleString('es-MX')}`
                                                            : '—'}
                                                    </td>
                                                    <td>{row.fecha_compra || '—'}</td>
                                                    <td>
                                                        <Badge bg={row.validation?.valid ? 'success' : 'danger'}>
                                                            {row.validation?.message}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    ) : (
                        <div className="table-responsive">
                            <Table bordered hover className="align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        {genericHeaders.map((header) => (
                                            <th key={header}>{header}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {activeSheet.rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={Math.max(genericHeaders.length + 1, 1)} className="text-center py-4">
                                                No hay datos cargados en esta pestaña.
                                            </td>
                                        </tr>
                                    ) : (
                                        activeSheet.rows.map((row) => (
                                            <tr key={`${activeSheet.name}-${row.rowNumber}`}>
                                                <td>{row.rowNumber}</td>
                                                {genericHeaders.map((header) => (
                                                    <td key={header}>
                                                        {row.values?.[header] ?? '—'}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};

export default ExcelReports;